/**
 * @fileoverview Regras de tela da triagem: estados ativos e finais, ações
 * permitidas em cada item (espelham as regras do backend, que confere de novo)
 * e formatação de datas no idioma da interface.
 */

import { i18n } from "../../translate/i18n";

export const ACTIVE_STATUSES = ["PENDING", "IN_PROGRESS", "WAITING_CUSTOMER"];
export const isActiveSession = (session) => !!session && ACTIVE_STATUSES.includes(session.status);

/**
 * Ações humanas que fazem sentido num item, conforme o estado (dentro do que o
 * backend permite). "edit" é concluir de novo com dados: corrige o que foi lido.
 */
export const itemActions = (status, hasFields) => {
  switch (status) {
    case "COMPLETED":
      return hasFields ? ["edit", "invalidate"] : ["invalidate"];
    case "WAIVED":
      return ["complete"];
    case "NOT_REQUESTED":
    case "INVALID":
      return ["complete", "request", "waive"];
    default:
      // Pedido, em análise ou aguardando reenvio.
      return ["complete", "invalidate", "request", "waive"];
  }
};

const LOCALES = { pt: "pt-BR", en: "en-US", es: "es-ES" };
export const locale = () => LOCALES[(i18n.language || "pt").slice(0, 2)] || "pt-BR";

/** Resposta de texto livre (item sem campos): o backend grava em "_answer". */
export const ANSWER_FIELD = "_answer";

/** CPF ou CNPJ com pontuação, como na nota-resumo (só é chamado com dado válido). */
const documento = (valor) => {
  const d = String(valor).replace(/[.\-/\s]/g, "").toUpperCase();
  if (/^\d{11}$/.test(d)) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
  if (/^[0-9A-Z]{12}\d{2}$/.test(d)) return d.replace(/^(.{2})(.{3})(.{3})(.{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  return String(valor);
};

/** Data "AAAA-MM-DD" no formato do idioma, sem passar pelo fuso (não vira o dia anterior). */
export const formatLocalDate = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (!m) return String(value);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString(locale());
};

/** Dados lidos de um item, na ordem dos campos da configuração (dado manual, da OS ou da IA). */
export const itemValues = (req) => {
  if (!req.extractedData || typeof req.extractedData !== "object") return [];
  const campos = req.fields || [];
  const ordem = (k) => {
    const i = campos.findIndex((f) => f.key === k);
    return i === -1 ? campos.length : i;
  };
  return Object.entries(req.extractedData)
    .filter(([, v]) => v && v.value !== undefined && v.value !== null && v.value !== "")
    .sort(([a], [b]) => ordem(a) - ordem(b))
    .map(([k, v]) => {
      const campo = campos.find((f) => f.key === k);
      const valido = v.valid !== false;
      let texto = String(v.value);
      if (valido && campo?.type === "cpf_cnpj") texto = documento(texto);
      if (valido && campo?.type === "date") texto = formatLocalDate(texto);
      const confianca =
        v.source !== "manual" && typeof v.confidence === "number" && v.confidence < 1
          ? `${Math.round(v.confidence * 100)}%`
          : null;
      return {
        key: k,
        // Resposta de texto livre: o valor já é a resposta, sem rótulo.
        label: k === ANSWER_FIELD ? null : campo?.label || k,
        value: texto,
        confidence: confianca,
        invalid: !valido,
      };
    });
};

export const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(locale(), { dateStyle: "short", timeStyle: "short" });
};

export const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale());
};

/**
 * Frase traduzida com um texto livre dentro ("Motivo: …", "Nota: …"). O texto
 * não passa pela interpolação do i18next, que apagaria um "{{2}}" ou leria um ":"
 * escrito pela pessoa.
 */
export const withText = (key, param, value) => {
  const MARCA = "\u2063";
  return i18n.t(key, { [param]: MARCA }).split(MARCA).join(String(value ?? ""));
};

/** Dados de uma regra de encaminhamento por extenso: "Garantia", "Garantia e Categoria". */
export const routeFieldsText = (fields) => {
  const nomes = fields.map((f) => label(`triage.manager.editor.routes.fields.${f}`, f || ""));
  if (nomes.length <= 1) return nomes.join("");
  return `${nomes.slice(0, -1).join(", ")} ${i18n.t("triage.manager.editor.routes.joinLast")} ${nomes[nomes.length - 1]}`;
};

/** Motivos que a IA grava como código (triage.attention.*). */
export const ATTENTION_CODES = [
  "customer_requested_human",
  "customer_refused",
  "cannot_provide",
  "max_attempts",
  "ai_unavailable",
  "ai_limit",
  "send_failed",
  "ticket_with_human",
  "no_response",
  "no_reply_to_reminders",
  "ai_quota",
  "billing_suspended",
];

/**
 * Tom de cor de cada estado (styles: ok, info, wait, bad, neutral). Verde: chegou
 * (ou a triagem terminou); azul: a IA está lendo ou conduzindo; âmbar: espera o
 * cliente; vermelho: não serviu ou falhou; neutro: fora do caminho (dispensado,
 * ainda não pedido, cancelado).
 */
export const REQUIREMENT_TONE = {
  COMPLETED: "ok",
  PROCESSING: "info",
  REQUESTED: "wait",
  WAITING_RESEND: "wait",
  INVALID: "bad",
  WAIVED: "neutral",
  NOT_REQUESTED: "neutral",
};

export const SESSION_TONE = {
  COMPLETED: "ok",
  IN_PROGRESS: "info",
  WAITING_CUSTOMER: "wait",
  FAILED: "bad",
  PENDING: "neutral",
  CANCELLED: "neutral",
};

export const EVENT_TONE = {
  TRIAGE_COMPLETED: "ok",
  REQUIREMENT_VALIDATED: "ok",
  REQUIREMENT_MANUALLY_COMPLETED: "ok",
  TRIAGE_STARTED: "info",
  TRIAGE_HANDOFF: "info",
  REQUIREMENT_RECEIVED: "info",
  ERROR_CODE_DETECTED: "info",
  AI_RESUMED: "info",
  REQUIREMENT_REQUESTED: "wait",
  OUTREACH_SENT: "wait",
  FOLLOWUP_SENT: "wait",
  FOLLOWUP_SKIPPED: "neutral",
  REQUIREMENT_REJECTED: "bad",
  TRIAGE_FAILED: "bad",
  OUTREACH_FAILED: "bad",
  ERROR: "bad",
  CUSTOMER_EXPORTED: "ok",
  CUSTOMER_EXPORT_SKIPPED: "wait",
  CUSTOMER_EXPORT_FAILED: "bad",
};

export const toneOf = (map, status) => map[status] || "neutral";

/** Rótulo traduzido com reserva no próprio código (evento novo sem tradução não quebra). */
export const label = (key, fallback) => {
  const text = i18n.t(key);
  return text === key ? fallback : text;
};
